import * as React from "react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChatSession } from "../../types";
import { PanelIcon, NewChatIcon, SearchIcon } from "../icons";
import { SessionItem } from "./components/SessionItem";
import styles from "./SessionSidebar.module.css";

interface Props {
	sessions: ChatSession[];
	currentSessionId: string;
	onSwitch: (id: string) => void;
	onCreate: () => void;
	onDelete: (id: string) => void;
	onRename: (id: string, name: string) => void;
	onToggleFavourite: (id: string) => void;
	/** Optional brand logo shown top-left of the sidebar. */
	logoUrl?: string;
	/** Collapses the sidebar (renders the collapse button when provided). */
	onCollapse?: () => void;
}

interface Group {
	label: string;
	sessions: ChatSession[];
}

// Groups the non-favourite sessions by recency (favourites are surfaced separately).
const groupByDate = (sessions: ChatSession[]): Group[] => {
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
	const yesterday = today - 24 * 60 * 60 * 1000;
	const sevenDaysAgo = today - 7 * 24 * 60 * 60 * 1000;

	const groups: Record<"Today" | "Yesterday" | "Previous 7 days" | "Earlier", ChatSession[]> = {
		Today: [],
		Yesterday: [],
		"Previous 7 days": [],
		Earlier: [],
	};

	const sorted = [...sessions].sort(
		(a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime()
	);

	for (const s of sorted) {
		const t = new Date(s.lastActivityAt).getTime();
		if (t >= today) groups["Today"].push(s);
		else if (t >= yesterday) groups["Yesterday"].push(s);
		else if (t >= sevenDaysAgo) groups["Previous 7 days"].push(s);
		else groups["Earlier"].push(s);
	}

	return Object.entries(groups)
		.filter(([, xs]) => xs.length > 0)
		.map(([label, xs]) => ({ label, sessions: xs }));
};

export const SessionSidebar: React.FC<Props> = ({
	sessions,
	currentSessionId,
	onSwitch,
	onCreate,
	onDelete,
	onRename,
	onToggleFavourite,
	logoUrl,
	onCollapse,
}) => {
	const [searching, setSearching] = useState(false);
	const [query, setQuery] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);

	// Glider: a purple indicator on the nav-item column that follows the hovered row and
	// rests on the active one (Search chats while searching). Ported from the CORE sidebar.
	const searchBtnRef = useRef<HTMLButtonElement>(null);
	const [glider, setGlider] = useState({ y: 0, h: 0, visible: false });

	const moveGliderTo = useCallback((el: HTMLElement | null) => {
		if (!el) return;
		setGlider({ y: el.offsetTop, h: el.offsetHeight, visible: true });
	}, []);

	// Rest on the active row when searching, otherwise hide when the pointer isn't over an item.
	const restGlider = useCallback(() => {
		if (searching) moveGliderTo(searchBtnRef.current);
		else setGlider((g) => ({ ...g, visible: false }));
	}, [searching, moveGliderTo]);

	useLayoutEffect(() => {
		restGlider();
	}, [restGlider]);

	const gliderStyle: React.CSSProperties = {
		transform: `translateY(${glider.y}px)`,
		height: `${glider.h}px`,
		opacity: glider.visible ? 1 : 0,
	};

	const openSearch = () => {
		setSearching(true);
		requestAnimationFrame(() => searchRef.current?.focus());
	};

	const closeSearch = () => {
		setSearching(false);
		setQuery("");
	};

	const { favourites, dateGroups } = useMemo(() => {
		const q = query.trim().toLowerCase();
		const filtered = q ? sessions.filter((s) => (s.name || "").toLowerCase().includes(q)) : sessions;
		const favs = filtered
			.filter((s) => s.favourite)
			.sort((a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime());
		return { favourites: favs, dateGroups: groupByDate(filtered.filter((s) => !s.favourite)) };
	}, [sessions, query]);

	const groups: Group[] = favourites.length > 0 ? [{ label: "Favourites", sessions: favourites }, ...dateGroups] : dateGroups;
	const isEmpty = groups.length === 0;

	const renderItem = (s: ChatSession) => (
		<SessionItem
			key={s.id}
			session={s}
			active={s.id === currentSessionId}
			onSwitch={onSwitch}
			onDelete={onDelete}
			onRename={onRename}
			onToggleFavourite={onToggleFavourite}
		/>
	);

	return (
		<aside className={styles.base}>
			<div className={styles.header}>
				{logoUrl ? (
					<img src={logoUrl} alt="" className={styles.logo} />
				) : (
					<span className={styles.brand}>Conversations</span>
				)}
				{onCollapse && (
					<button type="button" className={styles.iconBtn} onClick={onCollapse} aria-label="Collapse sidebar">
						<PanelIcon />
					</button>
				)}
			</div>

			<div className={styles.ctas} onMouseLeave={restGlider}>
				<span className={styles.gliderTrack} aria-hidden />
				<span className={styles.glider} style={gliderStyle} aria-hidden />
				<button
					type="button"
					className={styles.cta}
					onClick={onCreate}
					onMouseEnter={(e) => moveGliderTo(e.currentTarget)}
				>
					<NewChatIcon />
					<span>New chat</span>
				</button>
				<button
					ref={searchBtnRef}
					type="button"
					className={`${styles.cta}${searching ? ` ${styles.ctaActive}` : ""}`}
					onClick={() => (searching ? closeSearch() : openSearch())}
					onMouseEnter={(e) => moveGliderTo(e.currentTarget)}
					aria-pressed={searching}
				>
					<SearchIcon />
					<span>Search chats</span>
				</button>
			</div>

			{searching && (
				<div className={styles.searchRow}>
					<input
						ref={searchRef}
						className={styles.search}
						value={query}
						placeholder="Search conversations…"
						aria-label="Search conversations"
						onChange={(e) => setQuery(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Escape") closeSearch();
						}}
					/>
				</div>
			)}

			<div className={styles.list}>
				{isEmpty && (
					<div className={styles.empty}>{query ? "No matches." : "No conversations yet."}</div>
				)}
				{groups.map((g) => (
					<div key={g.label} className={styles.group}>
						<div className={styles.groupLabel}>{g.label}</div>
						{g.sessions.map(renderItem)}
					</div>
				))}
			</div>
		</aside>
	);
};
